import i18n, { LOCALIZED_PACK_UNITS } from '@/utils/i18n';
import MinusIcon from '@assets/icons/minus.svg';
import PlusIcon from '@assets/icons/plus.svg';
import React, { useMemo } from 'react';
import { AppModalStackParams } from '@/navigators/types';
import {
  BORDER,
  BORDER_RADIUS_SMALL,
  FONTS,
  SCREEN_PADDING_SMALL,
  TYPOGRAPHY,
} from '@/styles';
import { Button } from '@/components/button';
import { CHECK_IN_ROUTES } from '@/navigators/routes';
import { CheckInItem } from '@/contexts/check-in-context-types';
import { computeProgressPercent } from '@/utils/order-check-in';
import { createStyles } from '@/contexts/theme-context';
import {
  DECREMENT_HIT_SLOP,
  INCREMENT_HIT_SLOP,
} from '@/components/quantity-selector';
import { formatCurrency } from '@/utils/money';
import { ListDisplaySize } from '@/contexts/order-guide-settings-context';
import { OrderableItemListItemImage } from '@/components/orders/orderable-item-list-item-image';
import { OrderCheckInIssuesList } from '@/components/order-check-in/order-check-in-issues-list';
import {
  PixelRatio,
  StyleProp,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useItemDisplayOptions } from '@/contexts/new-order-context';
import { useNavigation } from '@react-navigation/native';

interface Props {
  checkInItem: CheckInItem;
  containerStyle?: StyleProp<ViewStyle>;
  hideProgressBar?: boolean;
  onItemQuantityUpdate: (itemUUID: string, quantity: number) => void;
  quantity: number;
}

type NavigationType = StackNavigationProp<
  AppModalStackParams,
  CHECK_IN_ROUTES.CheckInReportAnIssue
>;

const fontScale = PixelRatio.getFontScale();
const ICON_SIZE = 26 * fontScale;
const UPDATE_QUANTITY_BUTTON_SIZE = 56 * fontScale;

export const OrderCheckInSingleItemComponent: React.FC<Props> = ({
  checkInItem,
  containerStyle,
  hideProgressBar = false,
  onItemQuantityUpdate,
  quantity,
}) => {
  const { hidePlaceholderImages } = useItemDisplayOptions();
  const [styles, theme] = useStyles();
  const { navigate } = useNavigation<NavigationType>();
  const {
    amountOfIssues,
    checkInItemUUID,
    checkInUUID,
    currencyCode,
    manufacturerSuggestedRetailPriceInMicros,
    issues,
    shipQuantity,
    title,
    variantPack,
  } = checkInItem;

  const incrementQuantity = () => {
    onItemQuantityUpdate(checkInItemUUID, quantity + 1);
  };
  const decrementQuantity = () => {
    onItemQuantityUpdate(checkInItemUUID, quantity - 1);
  };

  const reportAnIssue = () => {
    navigate(CHECK_IN_ROUTES.CheckInReportAnIssue, {
      checkInItem,
      shouldRenderBackIcon: true,
    });
  };

  const progressBarWidth = useMemo(
    () => computeProgressPercent(quantity, shipQuantity),
    [quantity, shipQuantity],
  );

  const reportIssueDisabled = shipQuantity === amountOfIssues;
  const allItemsHaveIssues = quantity === amountOfIssues;

  const subtitle = useMemo(() => {
    if (!variantPack?.metadata) {
      return null;
    }

    const { externalID, packUnit } = variantPack.metadata;
    const displayUnit = LOCALIZED_PACK_UNITS[packUnit] || packUnit;
    const formattedMSRP = manufacturerSuggestedRetailPriceInMicros
      ? i18n.t('msrp_price', {
          price: formatCurrency(
            manufacturerSuggestedRetailPriceInMicros,
            currencyCode,
          ),
        })
      : null;

    return (
      <Text
        numberOfLines={1}
        style={[TYPOGRAPHY.small, FONTS.primary, styles.subtitle]}
      >
        {externalID ? `${externalID} • ` : ''}
        <Text style={styles.capitalizedText}>{displayUnit}</Text>
        {formattedMSRP ? ` • ${formattedMSRP}` : ''}
      </Text>
    );
  }, [
    currencyCode,
    manufacturerSuggestedRetailPriceInMicros,
    variantPack,
    styles,
  ]);

  return (
    <>
      {hideProgressBar ? null : (
        <View
          style={styles.progressBarOuter}
          testID={`progress-bar-${progressBarWidth}%`}
        >
          <View
            style={[
              styles.progressBarInner,
              {
                backgroundColor: issues?.length
                  ? theme.sk.color.icon.warning.secondary
                  : theme.sk.color.bg.highlight.primary,
                width: `${progressBarWidth}%`,
              },
            ]}
          />
        </View>
      )}
      <View style={[styles.checkInItemWrapper, containerStyle]}>
        <View style={styles.imageAndContentWrapper}>
          <View style={styles.photoContainer}>
            <OrderableItemListItemImage
              alt={`image-${checkInItemUUID}`}
              hidePlaceholderImages={hidePlaceholderImages}
              imageURLs={variantPack?.metadata?.photoURLs ?? []}
              listDisplaySize={ListDisplaySize.Huge}
            />
          </View>
          <View style={styles.textContainer}>
            <Text
              numberOfLines={2}
              style={[FONTS.primarySemiBold, styles.textCenter, styles.title]}
            >
              {variantPack?.title ?? title}
            </Text>
            {subtitle ? (
              <Text
                numberOfLines={2}
                style={[TYPOGRAPHY.description, styles.textCenter]}
              >
                {subtitle}
              </Text>
            ) : null}
          </View>
        </View>
        <View style={styles.bottomRow}>
          <View style={styles.quantitySelector}>
            <TouchableOpacity
              disabled={quantity === 0 || allItemsHaveIssues}
              focusable={false}
              hitSlop={DECREMENT_HIT_SLOP}
              onPress={decrementQuantity}
              style={[
                styles.quantityButton,
                quantity === 0 || allItemsHaveIssues
                  ? styles.quantityButtonDisabled
                  : null,
              ]}
              testID="decrement-quantity"
            >
              <MinusIcon
                fill={theme.colors.accentText}
                height={ICON_SIZE}
                testID="minus-icon"
                width={ICON_SIZE}
              />
            </TouchableOpacity>
            <Text style={[TYPOGRAPHY.h1]}>{`${quantity}/${shipQuantity}`}</Text>
            <TouchableOpacity
              disabled={quantity === shipQuantity}
              focusable={false}
              hitSlop={INCREMENT_HIT_SLOP}
              onPress={incrementQuantity}
              style={[
                styles.quantityButton,
                quantity === shipQuantity
                  ? styles.quantityButtonDisabled
                  : null,
              ]}
              testID="increment-quantity"
            >
              <PlusIcon
                fill={theme.colors.accentText}
                height={ICON_SIZE}
                width={ICON_SIZE}
              />
            </TouchableOpacity>
          </View>
          <Button
            disabled={reportIssueDisabled}
            onPress={reportAnIssue}
            size="small"
            style={[
              styles.reportAnIssue,
              { height: UPDATE_QUANTITY_BUTTON_SIZE },
            ]}
            textStyle={
              reportIssueDisabled
                ? styles.reportAnIssueLabelDisabled
                : styles.reportAnIssueLabel
            }
            trackingData={{
              name: 'check_in_report_an_issue',
              properties: {
                checkInItemUUID: checkInItemUUID,
              },
            }}
            type="tertiary"
          >
            {i18n.t('check_in.report_an_issue')}
          </Button>
        </View>
        {issues?.length ? (
          <View style={styles.issuesList}>
            <OrderCheckInIssuesList
              checkInItemUUID={checkInItemUUID}
              checkInUUID={checkInUUID}
              issues={issues}
            />
          </View>
        ) : null}
      </View>
    </>
  );
};

const useStyles = createStyles((theme) => ({
  bottomRow: {
    alignItems: 'center',
    gap: 24,
  },
  capitalizedText: {
    textTransform: 'capitalize',
  },
  checkInItemWrapper: {
    flex: 1,
    gap: 48,
    justifyContent: 'center',
    paddingHorizontal: SCREEN_PADDING_SMALL,
  },
  imageAndContentWrapper: {
    alignItems: 'center',
    gap: 16,
  },
  issuesList: {
    backgroundColor: theme.sk.color.bg.secondary.default,
    borderRadius: BORDER_RADIUS_SMALL,
  },
  photoContainer: {
    ...BORDER.primary,
    overflow: 'hidden',
  },
  progressBarInner: {
    height: 3,
  },
  progressBarOuter: {
    backgroundColor: theme.sk.color.bg.secondary.hover,
    height: 3,
  },
  quantityButton: {
    alignItems: 'center',
    backgroundColor: theme.colors.accent,
    borderRadius: UPDATE_QUANTITY_BUTTON_SIZE,
    height: UPDATE_QUANTITY_BUTTON_SIZE,
    justifyContent: 'center',
    width: UPDATE_QUANTITY_BUTTON_SIZE,
  },
  quantityButtonDisabled: {
    opacity: 0.2,
  },
  quantitySelector: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 16,
  },
  reportAnIssue: {
    paddingHorizontal: 4,
  },
  reportAnIssueLabel: {
    ...FONTS.primarySemiBold,
    color: theme.sk.color.text.tertiary,
    fontSize: theme.fontSizes.small,
  },
  reportAnIssueLabelDisabled: {
    ...FONTS.primarySemiBold,
    color: theme.sk.color.text.disabled,
    fontSize: theme.fontSizes.small,
  },
  subtitle: {
    color: theme.sk.color.text.tertiary,
  },
  textCenter: {
    textAlign: 'center',
  },
  textContainer: {
    gap: 8,
  },
  title: {
    fontSize: 21,
  },
}));
